import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';

import { Typography, LazyAutocompleteFieldNotMultiple, InfoBox } from '@occc/ui-design-system';
import { useGetAudiencesLazy } from '@occc-app/dataAccessLayer/audience/queries';
import { useGetAudienceById } from '@occc-app/dataAccessLayer/audience/useGetAudienceById';
import { FixedDateRangeAudienceWarning } from '@occc-app/routes/Audience/components/FixedDateRangeAudienceWarning';
import { hasFixedDateRange, isAudienceActiveButNotReady } from '@occc-app/routes/Audience/utils';

import { InAudienceConstraintProps, Audience } from './types';
import {
  makeAudienceFilter,
  mapAudiencesQueryResponseToRawArray,
  mapRawAudiencesResponseItemToAutocompleteOption,
} from './utils';

const DEFAULT_MEMBERSHIP_COUNT_INFO_TITLE = 'Approximate Audience size';

export const InAudienceConstraint = ({
  values,
  chainId,
  disabled,
  infoTitle = DEFAULT_MEMBERSHIP_COUNT_INFO_TITLE,
  isNoTargetingAttributesWarningEnabled = false,
}: InAudienceConstraintProps) => {
  const isWarningVisible = useMemo(
    () =>
      !!values.inAudience?.audience &&
      isAudienceActiveButNotReady(
        values.inAudience.audience.status,
        values.inAudience.audience.hasBeenBackFilled,
        hasFixedDateRange(values.inAudience.audience.targetingAttributes),
      ),
    [values.inAudience?.audience],
  );

  const hasTargetingAttributes = useMemo(
    () =>
      Boolean(
        !!values.inAudience?.audience && values.inAudience.audience.targetingAttributes?.length,
      ),
    [values.inAudience?.audience],
  );

  const isNoTargetingAttributesWarningVisible = useMemo(
    () =>
      isNoTargetingAttributesWarningEnabled &&
      Boolean(values.inAudience?.audience) && // Check if audience is selected
      !hasTargetingAttributes,
    [values.inAudience?.audience, isNoTargetingAttributesWarningEnabled, hasTargetingAttributes],
  );

  const audienceIdToFetchData =
    values.inAudience?.audience?.membershipCount === undefined
      ? values.inAudience?.audience?.uuid
      : undefined;

  const { data: audience } = useGetAudienceById(audienceIdToFetchData);

  const membershipCount = values.inAudience?.audience?.membershipCount ?? audience?.membershipCount;

  const makeQueryFilter = React.useMemo(() => {
    return makeAudienceFilter(chainId);
  }, [chainId]);

  return (
    <>
      <Typography variant='h6' paragraph>
        Target members in this audience
      </Typography>
      <LazyAutocompleteFieldNotMultiple<
        Audience,
        ReturnType<typeof useGetAudiencesLazy>['response']
      >
        name='inAudience.audience'
        label='Select an Audience'
        useLazyQuery={useGetAudiencesLazy}
        makeQueryFilter={makeQueryFilter}
        mapQueryResponseToRawArray={mapAudiencesQueryResponseToRawArray}
        mapRawResponseItemToAutocompleteOption={mapRawAudiencesResponseItemToAutocompleteOption}
        inputProps={{ margin: 'none' }}
        first={100}
        disabled={disabled}
      />
      <Stack spacing={2} my={2}>
        {membershipCount !== undefined && membershipCount > 0 && (
          <InfoBox>
            <Typography>
              {infoTitle}:
              <Typography variant='subtitle1' component='span' color='primary'>
                {` ${membershipCount} `}
              </Typography>
              members
            </Typography>
          </InfoBox>
        )}
        {isWarningVisible && (
          <Box mt={2}>
            <FixedDateRangeAudienceWarning />
          </Box>
        )}
        {isNoTargetingAttributesWarningVisible && (
          <InfoBox type='warning'>
            <Typography color='error.dark'>
              Heads up! You're about to send this message to ALL members of the program.
            </Typography>
          </InfoBox>
        )}
      </Stack>
    </>
  );
};
